import React, { useEffect, useState } from 'react'
import PlanCard from './PlanCard';
import "./PlanSelector.css";
import { decryptData } from '../../common/encryptAnddecryptData';
import { ChatGptPaymentDetails, getAllPointPlans, initiateCashFreePayment } from '../../Api/commonAPI';
import { cashfree } from '../../App';
import { useSnackbar } from 'notistack';
import TotalPrice from './TotalPrice';

const Plan = ({ userDetails, token, setShowModal, setShouldRefetchPoints }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [planList, setPlanList] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState("");
    const [orderId, setOrderId] = useState("");
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [open, setOpen] = useState(false)
    const [total, setTotal] = useState(null);
    const [isLoading, setIsLoading] = useState(false)
    const [isModalClosing, setIsModalClosing] = useState(false);

    useEffect(() => {
        const fetchPlan = async () => {
            try {
                const response = await getAllPointPlans();
                const data = response.data;
                const decrypted = decryptData(data?.data);
                setPlanList(decrypted?.Plan || []);
            } catch (error) {
                console.error("Error fetching plans:", error);
            }
        };

        fetchPlan();
    }, []);

    useEffect(() => {
        if (sessionId) {
            const handleCheckout = async () => {
                try {
                    const checkoutOptions = {
                        paymentSessionId: sessionId,
                        redirectTarget: "_modal",
                    };

                    const result = await cashfree.checkout(checkoutOptions);

                    if (result.error) {
                        setPaymentStatus("error");
                        return;
                    }

                    if (result.paymentDetails) {
                        setPaymentStatus("success");
                        const response = await ChatGptPaymentDetails(
                            userDetails?.CustomerID,
                            selectedId,
                            orderId,
                            token,
                        );

                        if (response?.status === 200) {
                            setShouldRefetchPoints(true);
                            enqueueSnackbar("Successfully purchased points", { variant: 'success' });
                            handleCloseModal();
                        }
                    }
                } catch (error) {
                    setPaymentStatus("error");
                    console.error("Payment failed:", error);
                }
            };

            handleCheckout();
        }
    }, [sessionId]);

    const handleCloseModal = () => {
        setIsModalClosing(true);
        setTimeout(() => {
            setShowModal(false);
            setIsModalClosing(false);
        }, 300);
    };

    const handleBuy = async () => {
        if (!selectedId) {
            enqueueSnackbar("Please select a plan before proceeding.", { variant: 'warning' });
            return;
        }

        try {
            setLoading(true);
            setOpen(true);
        } catch (error) {
            console.error("Payment error:", error);
            enqueueSnackbar("An error occurred during the payment process.", { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handlePurchase = async () => {
        try {
            const customerId = userDetails?.CustomerID;
            const TotalAmount = total?.TotalAmount;
            setIsLoading(true);
            const response = await initiateCashFreePayment(
                selectedId,
                customerId,
                token,
                TotalAmount,
            );

            const decrypted = decryptData(response.data?.data);
            setSessionId(decrypted?.payment_session_id);
            setOrderId(decrypted?.order_id);

        } catch (error) {
            console.error('error', error);
            enqueueSnackbar("Payment initiation failed. Please try again.", { variant: 'error' });
        }
        finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {planList?.length > 0 && (
                <div className={`modal-backdrop fade ${isModalClosing ? 'closing' : 'show'}`}>
                    <div className="modal-container">
                        <div className={`modal-content ${isModalClosing ? 'closing' : ''}`}>
                            <div className="modal-header">
                                <div className="title-section">
                                    <h2 className="modal-title">Choose Your Plan</h2>
                                    <p className="modal-subtitle">Select the plan that best fits your needs</p>
                                </div>
                                <button 
                                    type="button" 
                                    className="close-button" 
                                    onClick={handleCloseModal}
                                    aria-label="Close modal"
                                >
                                    X
                                </button>
                            </div>
                            
                            <div className="modal-body">
                                <div className="plans-grid">
                                    {planList.map((plan, index) => (
                                        <div key={plan.pointPlanID} className="plan-card-wrapper">
                                            <PlanCard
                                                plan={plan}
                                                selected={plan.pointPlanID === selectedId}
                                                onSelect={setSelectedId}
                                                index={index}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="modal-footer">
                                <div className="footer-content">
                                    <div className="selected-plan-info">
                                        {selectedId ? (
                                            <>
                                                <span className="selected-label">Selected Plan:</span>
                                                <span className="selected-plan-name">
                                                    {planList.find(p => p.pointPlanID === selectedId)?.planName}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="no-plan-selected">No plan selected</span>
                                        )}
                                    </div>
                                    <div className="action-buttons">
                                        <button 
                                            className="secondary-button" 
                                            onClick={handleCloseModal}
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            className="primary-button" 
                                            onClick={handleBuy} 
                                            disabled={loading || !selectedId}
                                        >
                                            {loading ? (
                                                <span className="button-loading">
                                                    <span className="spinner"></span>
                                                    Processing...
                                                </span>
                                            ) : (
                                                'Continue to Payment'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {open && (
                <TotalPrice 
                    userDetails={userDetails} 
                    token={token} 
                    selectedId={selectedId} 
                    setOpen={setOpen} 
                    planList={planList} 
                    setTotal={setTotal} 
                    total={total} 
                    handlePurchase={handlePurchase} 
                    isLoading={isLoading}
                />
            )}
        </>
    );
}

export default Plan;